/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * PendingAnalysisRail — always-visible "your review is being
 * analysed" continuity surface.
 *
 * P0 (May 20, 2026 — iPad operational continuity failure):
 *   Operator report: "videos selected/uploaded successfully → system
 *   confirms analysis will start → then the review vanishes from the
 *   user's visible flow → no persistent pending-analysis state → no
 *   visible active operation → no obvious return path → no reassuring
 *   continuity → feels like the upload has disappeared permanently."
 *
 * Root cause:
 *   • `/lesson-review` filters its `PendingReviewQueue` to NON-learners
 *     (line 1696 of LessonReview.js). Learners get nothing.
 *   • Basket `onHandedOff` shows a 9-second toast then returns the
 *     user to the start screen with no inline pending state.
 *   • `ActiveOperationPill` is gated to mobile ≤640px — iPad falls
 *     through to a "bell-dot replaces it" silent fallback that is
 *     visually missable.
 *   • `/lesson-review` is on the pill suppression list anyway.
 *
 * This rail fixes the "vanish" — it is ALWAYS rendered (when any
 * review is in flight) at the top of `/lesson-review` regardless of
 * role, regardless of viewport. One row per processing review with
 * status + tap-to-view CTA.
 *
 * Doctrine pin (May 20, 2026): "Never ghost the user. Uploads/reviews
 * cannot disappear silently."
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Brain, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import { loadRecentReview, clearRecentReview } from "../../lib/recentReviewRecovery";
import { legacyUploadCompatEnabled } from "../../lib/legacyUploadCompat";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const IN_FLIGHT_STATUSES = new Set([
  "queued",
  "queued_for_processing",
  "extracting_audio",
  "transcribing",
  "transcribed",
  "analysing",
  "grading",
  "processing",
  "stitching",
  "paid",            // freshly paid, server is about to pick it up
]);

const RECOVERABLE_FAILURE_STATUSES = new Set([
  "failed",
  "error",
]);

const STATUS_LABEL = {
  queued:                 "Queued for analysis",
  queued_for_processing:  "Queued for analysis",
  extracting_audio:       "Extracting audio…",
  transcribing:           "Transcribing…",
  transcribed:            "Transcribed · grading next",
  analysing:              "Analysing your lesson…",
  grading:                "Grading your lesson…",
  processing:             "Processing…",
  stitching:              "Stitching clips together…",
  paid:                   "Paid · queuing analysis…",
  failed:                 "Processing failed — tap to recover",
  error:                  "Processing failed — tap to recover",
};

function isInFlight(s) { return IN_FLIGHT_STATUSES.has(String(s || "").toLowerCase()); }
function isFailed(s)   { return RECOVERABLE_FAILURE_STATUSES.has(String(s || "").toLowerCase()); }

// F-3 fallback classifier (client-side mirror of the server's
// _classify_review_phase). Used only when the server didn't project
// lifecycle_phase (older bundle), to keep auto-clear behaviour
// consistent.
function _classifyClientside(status) {
  const s = String(status || "").toLowerCase();
  if (s === "complete" || s === "completed") return "complete";
  if (s === "failed" || s === "error") return "failed_retryable";
  if (s === "queued" || s === "queued_for_processing" || s === "paid") return "queued";
  if (IN_FLIGHT_STATUSES.has(s)) return "active";
  return "queued";
}

function shortTitle(item) {
  const t = item?.display_title || item?.title || item?.filename;
  if (t) return String(t).slice(0, 60);
  if (item?.created_at) {
    try {
      const d = new Date(item.created_at);
      return `Lesson · ${d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`;
    } catch (_) { /* noop */ }
  }
  return `Review · ${String(item?.id || "").slice(0, 8)}`;
}

export default function PendingAnalysisRail({
  user = null,
  onOpen,
  testIdPrefix = "pending-analysis-rail",
  className = "",
}) {
  const [activeItems, setActiveItems] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine — don't poll /api/lesson-review/list.
      setActiveItems([]);
      setNeedsAttention([]);
      setCounts(null);
      setErrored(false);
      setLoading(false);
      return;
    }
    try {
      // F-1 May 21, 2026 — pass `days=7` so we exclude operationally
      // dead ancient reviews ("21 ANALYSES IN PROGRESS" symptom).
      // The backend reaper also runs on /list invocation to migrate
      // any in-flight rows older than 2h to terminal failed state.
      const [listResp, summaryResp] = await Promise.all([
        axios.get(`${API}/api/lesson-review/list?limit=30&days=7`, {
          withCredentials: true,
        }),
        axios.get(`${API}/api/lesson-review/summary`, {
          withCredentials: true,
        }).catch(() => ({ data: null })),
      ]);
      const all = listResp?.data?.items || [];

      // F-2 May 21, 2026 — visual split. Use the server-projected
      // lifecycle_phase when present; fall back to the legacy
      // in-flight/failed classification for older bundles.
      const active = [];
      const attention = [];
      for (const it of all) {
        const phase = it.lifecycle_phase
          || (isInFlight(it.status) ? "active" : isFailed(it.status) ? "failed_retryable" : null);
        if (phase === "active" || phase === "queued") {
          active.push(it);
        } else if (phase === "failed_retryable") {
          attention.push(it);
        }
        // stale | failed_terminal | complete are intentionally
        // EXCLUDED from the rail. They are not operationally in
        // progress and they are not the user's job to recover.
      }
      setActiveItems(active);
      setNeedsAttention(attention);
      setCounts(summaryResp?.data?.counts || null);
      setErrored(false);

      // F-3 May 21, 2026 — auto-clear stale localStorage pointers.
      // If the recent-review pointer references a reviewId we now
      // observe in a terminal state (complete OR failed_terminal OR
      // stale), drop the pointer so the Recent Review banner stops
      // ghosting.
      if (user) {
        try {
          const uid = user._id || user.id || user.email;
          const rec = loadRecentReview(uid, user?.role || null);
          if (rec && rec.reviewId) {
            const observed = all.find((it) => it.id === rec.reviewId);
            if (observed) {
              const phase = observed.lifecycle_phase || _classifyClientside(observed.status);
              if (phase === "complete" || phase === "failed_terminal" || phase === "stale") {
                clearRecentReview(uid, user?.role || null);
              }
            }
          }
        } catch (_) { /* never break the rail on localStorage */ }
      }
    } catch (_) {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPending();
    // Adaptive polling — active every 8s while there is at least one
    // in-flight item, idle every 30s otherwise.
    let id;
    const tick = () => {
      const anyInFlight = activeItems.length > 0;
      id = setTimeout(() => {
        fetchPending().then(tick);
      }, anyInFlight ? 8000 : 30_000);
    };
    tick();
    // LEGACY_DECOMMISSIONED (May 22, 2026): visibility + focus
    // listeners that auto-refetch pending analyses on Photos return
    // are gated by REACT_APP_LEGACY_UPLOAD_COMPAT (default OFF).
    // Polling (above) continues so the rail still self-updates;
    // only the global event listeners are quarantined.
    if (!legacyUploadCompatEnabled()) {
      return () => { if (id) clearTimeout(id); };
    }
    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        fetchPending();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", fetchPending);
    return () => {
      if (id) clearTimeout(id);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", fetchPending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPending]);

  if (loading) {
    return (
      <div
        data-testid={`${testIdPrefix}-loading`}
        className={`rounded-2xl border border-slate-700 bg-slate-900/60 p-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-slate-400 text-[12px]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Checking for analyses in progress…
        </div>
      </div>
    );
  }

  if (errored) {
    return (
      <div
        data-testid={`${testIdPrefix}-error`}
        className={`rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-amber-200 text-[12px]">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Couldn't load pending analyses. We'll try again.</span>
          <button
            type="button"
            data-testid={`${testIdPrefix}-retry`}
            onClick={fetchPending}
            className="ml-auto text-amber-200 underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (activeItems.length === 0 && needsAttention.length === 0) {
    return null;
  }

  // Media quarantine — silent. The fetch above already clears the
  // lists; this is the doctrine pin a future grep will land on.
  if (!MEDIA_FEATURE_ENABLED) return null;

  // F-5 source of truth — server-summary counts where available;
  // local counts as fallback for older bundles.
  const inProgressCount = counts?.in_progress_total ?? activeItems.length;
  const attentionCount = counts?.attention_total ?? needsAttention.length;

  return (
    <div
      data-testid={testIdPrefix}
      data-in-progress-count={inProgressCount}
      data-attention-count={attentionCount}
      className={`space-y-3 ${className}`}
      aria-live="polite"
    >
      {activeItems.length > 0 && (
        <section
          data-testid={`${testIdPrefix}-in-progress`}
          className="rounded-2xl border border-indigo-500/40 bg-indigo-500/[0.06] p-4"
        >
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center flex-none">
              <Brain className="w-4 h-4 text-indigo-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-200">
                Analyses in progress · {inProgressCount}
              </p>
              <p className="text-[12px] text-slate-300 leading-snug">
                Your lesson{inProgressCount === 1 ? " is" : "s are"} being
                analysed. You'll see the report card here when it's ready.
                Safe to leave this page.
              </p>
            </div>
          </header>
          <ul className="space-y-2">
            {activeItems.map((it) => (
              <RailRow
                key={it.id}
                it={it}
                testIdPrefix={testIdPrefix}
                variant="in_progress"
                onOpen={onOpen}
              />
            ))}
          </ul>
        </section>
      )}

      {needsAttention.length > 0 && (
        <section
          data-testid={`${testIdPrefix}-needs-attention`}
          className="rounded-2xl border border-rose-500/40 bg-rose-500/[0.08] p-4"
        >
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-400/30 flex items-center justify-center flex-none">
              <AlertCircle className="w-4 h-4 text-rose-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-rose-200">
                Needs attention · {attentionCount}
              </p>
              <p className="text-[12px] text-rose-100/85 leading-snug">
                These lesson{attentionCount === 1 ? "" : "s"} didn't finish
                analysing. Tap to retry — your upload is still here.
              </p>
            </div>
          </header>
          <ul className="space-y-2">
            {needsAttention.map((it) => (
              <RailRow
                key={it.id}
                it={it}
                testIdPrefix={testIdPrefix}
                variant="needs_attention"
                onOpen={onOpen}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function RailRow({ it, testIdPrefix, variant, onOpen }) {
  const failed = variant === "needs_attention";
  const label = STATUS_LABEL[it.status] || it.status || "Processing…";
  return (
    <li
      data-testid={`${testIdPrefix}-row`}
      data-review-id={it.id}
      data-review-status={it.status}
      data-row-variant={variant}
    >
      <button
        type="button"
        data-testid={`${testIdPrefix}-row-open`}
        onClick={() => onOpen?.(it)}
        className={`w-full text-left rounded-xl p-3 flex items-center gap-3 transition border ${
          failed
            ? "border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15"
            : "border-indigo-500/30 bg-slate-900/40 hover:bg-slate-900/60"
        }`}
      >
        <div className="flex-none w-7 h-7 rounded-md bg-slate-900 border border-slate-700 flex items-center justify-center">
          {failed ? (
            <AlertCircle className="w-3.5 h-3.5 text-rose-300" />
          ) : (
            <Loader2 className="w-3.5 h-3.5 text-indigo-300 animate-spin" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-extrabold text-white truncate">
            {shortTitle(it)}
          </p>
          <p
            className={`text-[11px] mt-0.5 leading-tight ${
              failed ? "text-rose-200" : "text-slate-400"
            }`}
          >
            {label}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 flex-none" />
      </button>
    </li>
  );
}
